import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import PageMeta from '@/components/feature/PageMeta';
import { categories } from '@/mocks/categories';
import { getHotComics, getLatestComics } from '@/mocks/comics';
import { faqs, stats } from '@/mocks/siteData';

function StarRating({ rating }: { rating: number }) {
  return (
    <div className="flex items-center gap-0.5">
      {[1, 2, 3, 4, 5].map((star) => (
        <span key={star} className="w-3.5 h-3.5 flex items-center justify-center">
          <i
            className={`text-xs ${
              star <= Math.floor(rating)
                ? 'ri-star-fill text-amber-400'
                : star - 0.5 <= rating
                ? 'ri-star-half-line text-amber-400'
                : 'ri-star-line text-foreground-300'
            }`}
          ></i>
        </span>
      ))}
      <span className="text-xs text-foreground-500 ml-1">{rating}</span>
    </div>
  );
}

export default function Home() {
  const hotComics = getHotComics(8);
  const latestComics = getLatestComics(8);

  const homepageSchema = {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    name: '韩漫大全 - 韩国漫画在线阅读平台',
    description: '通过韩漫官方网址访问韩国漫画平台，提供韩漫大全和好看韩国漫画APP，支持无删减漫画免费在线观看，体验流畅便捷的阅读服务。',
    url: 'https://www.hanman123.com/',
    inLanguage: 'zh-CN',
    isPartOf: {
      '@type': 'WebSite',
      name: '韩漫大全',
      url: 'https://www.hanman123.com/',
    },
    about: {
      '@type': 'Thing',
      name: '韩国漫画',
      description: '韩国漫画在线阅读平台，提供多种类型的韩国漫画免费在线观看',
    },
  };

  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqs.map((faq) => ({
      '@type': 'Question',
      name: faq.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: faq.answer,
      },
    })),
  };

  return (
    <>
      <PageMeta
        title="韩国漫画-韩漫大全-好看的韩国漫画APP-无删减漫画免费在线观看-韩漫官方网址"
        description="通过韩漫官方网址访问韩国漫画平台，提供韩漫大全和好看韩国漫画APP，支持无删减漫画免费在线观看，体验流畅便捷的阅读服务。涵盖恋爱、动作、奇幻、悬疑、搞笑等多种类型的韩国漫画。"
        keywords="韩国漫画,韩漫大全,好看韩国漫画APP,无删减漫画免费在线观看,韩漫官方网址"
        canonicalUrl="https://www.hanman123.com/"
      />
      <ScrollToTop />
      <Navbar />

      <main>
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: JSON.stringify(homepageSchema) }}
        />
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: JSON.stringify(faqSchema) }}
        />

        {/* Hero Section */}
        <section className="relative w-full h-[420px] md:h-[600px] flex items-center justify-center overflow-hidden">
          <img
            src="https://readdy.ai/api/search-image?query=A%20beautiful%20stylized%20anime%20style%20illustration%20of%20a%20cozy%20reading%20nook%20with%20floating%20comic%20book%20pages%20and%20soft%20warm%20amber%20lighting%20artistic%20minimalist%20background%20with%20abstract%20geometric%20patterns%20in%20warm%20terracotta%20and%20cream%20tones%20dreamy%20atmosphere%20elegant%20composition&width=1600&height=900&seq=hero-bg-home-01&orientation=landscape"
            alt="韩漫大全 - 韩国漫画在线阅读平台"
            className="absolute inset-0 w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/20 to-black/50"></div>
          <div className="relative z-10 text-center px-4 w-full max-w-4xl mx-auto">
            <h1 className="text-3xl md:text-5xl lg:text-6xl font-bold font-heading text-white mb-4 leading-tight">
              韩国漫画 · 韩漫大全
            </h1>
            <p className="text-base md:text-lg text-white/80 max-w-2xl mx-auto mb-8 leading-relaxed">
              通过<strong>韩漫官方网址</strong>访问最全的韩国漫画平台，提供<strong>韩漫大全</strong>和
              <strong>好看韩国漫画APP</strong>，支持<strong>无删减漫画免费在线观看</strong>，体验流畅便捷的阅读服务
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
              <Link
                to="/category/remen-tuijian"
                className="px-8 py-3 bg-primary-500 text-background-50 dark:text-foreground-950 text-sm font-semibold rounded-lg hover:bg-primary-600 transition-all whitespace-nowrap cursor-pointer"
              >
                <i className="ri-fire-line mr-2"></i>
                开始阅读
              </Link>
              <a
                href="#"
                className="px-8 py-3 bg-white/15 backdrop-blur-sm text-white text-sm font-semibold rounded-lg hover:bg-white/25 transition-all whitespace-nowrap cursor-pointer border border-white/20"
              >
                <i className="ri-download-line mr-2"></i>
                下载APP
              </a>
            </div>
          </div>
        </section>

        {/* Stats Bar */}
        <section className="bg-background-50 border-b border-background-200/70">
          <div className="w-full px-4 md:px-6 lg:px-10">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6 py-8">
              {stats.map((stat) => (
                <div key={stat.label} className="text-center">
                  <span className="w-10 h-10 flex items-center justify-center mx-auto mb-2 bg-accent-100/80 rounded-lg">
                    <i className={`${stat.icon} text-accent-600 text-lg`}></i>
                  </span>
                  <p className="text-xl md:text-2xl font-bold font-heading text-foreground-900">{stat.value}</p>
                  <p className="text-xs text-foreground-500 mt-1">{stat.label}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Category Navigation */}
        <section className="py-14 md:py-20 bg-background-100">
          <div className="w-full px-4 md:px-6 lg:px-10 max-w-7xl mx-auto">
            <div className="text-center mb-10">
              <h2 className="text-2xl md:text-3xl font-bold font-heading text-foreground-900 mb-3">
                漫画分类浏览
              </h2>
              <p className="text-sm text-foreground-500 max-w-lg mx-auto">
                涵盖多种类型的韩国漫画，总有一款适合你的口味
              </p>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
              {categories.map((cat) => (
                <Link
                  key={cat.slug}
                  to={`/category/${cat.slug}`}
                  className="group bg-background-50 rounded-xl p-5 text-center transition-colors hover:bg-primary-100/50 border border-background-200/70 hover:border-primary-200 cursor-pointer"
                >
                  <span className="w-12 h-12 flex items-center justify-center mx-auto mb-3 bg-primary-100/70 rounded-xl group-hover:bg-primary-200 transition-colors">
                    <i className={`${cat.icon} text-xl text-primary-600`}></i>
                  </span>
                  <h3 className="text-sm font-bold font-heading text-foreground-800 mb-1">{cat.name}</h3>
                  <p className="text-xs text-foreground-500 line-clamp-2">{cat.description}</p>
                </Link>
              ))}
            </div>
          </div>
        </section>

        {/* Hot Comics */}
        <section className="py-14 md:py-20 bg-background-50" data-product-shop>
          <div className="w-full px-4 md:px-6 lg:px-10 max-w-7xl mx-auto">
            <div className="flex items-center justify-between mb-10">
              <div>
                <h2 className="text-2xl md:text-3xl font-bold font-heading text-foreground-900 mb-2">
                  <i className="ri-fire-line text-primary-500 mr-2"></i>
                  热门推荐
                </h2>
                <p className="text-sm text-foreground-500">最受读者欢迎的韩国漫画</p>
              </div>
              <Link
                to="/category/remen-tuijian"
                className="text-sm text-primary-600 hover:text-primary-700 font-medium flex items-center gap-1 cursor-pointer whitespace-nowrap"
              >
                查看全部 <i className="ri-arrow-right-line"></i>
              </Link>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-5">
              {hotComics.map((comic) => (
                <Link
                  key={comic.id}
                  to={`/article/${comic.id}`}
                  className="group cursor-pointer"
                >
                  <div className="relative aspect-[3/4] rounded-xl overflow-hidden mb-3 bg-background-100">
                    <img
                      src={comic.coverImage}
                      alt={`${comic.title} - 韩国漫画`}
                      title={`${comic.title} - 韩国漫画免费在线观看`}
                      className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-300"
                    />
                    <div className="absolute top-2 left-2">
                      <span className="px-2 py-0.5 bg-accent-500 text-background-50 text-xs font-medium rounded-md">
                        {comic.status}
                      </span>
                    </div>
                  </div>
                  <h3 className="text-sm font-bold font-heading text-foreground-900 group-hover:text-primary-600 transition-colors line-clamp-1 mb-1">
                    {comic.title}
                  </h3>
                  <p className="text-xs text-foreground-500 mb-1.5">{comic.author}</p>
                  <StarRating rating={comic.rating} />
                </Link>
              ))}
            </div>
          </div>
        </section>

        {/* Latest Updates */}
        <section className="py-14 md:py-20 bg-background-100" data-product-shop>
          <div className="w-full px-4 md:px-6 lg:px-10 max-w-7xl mx-auto">
            <div className="flex items-center justify-between mb-10">
              <div>
                <h2 className="text-2xl md:text-3xl font-bold font-heading text-foreground-900 mb-2">
                  <i className="ri-timer-line text-accent-500 mr-2"></i>
                  最新更新
                </h2>
                <p className="text-sm text-foreground-500">最近更新的韩国漫画作品</p>
              </div>
              <Link
                to="/category/remen-tuijian"
                className="text-sm text-primary-600 hover:text-primary-700 font-medium flex items-center gap-1 cursor-pointer whitespace-nowrap"
              >
                查看全部 <i className="ri-arrow-right-line"></i>
              </Link>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {latestComics.map((comic, index) => (
                <Link
                  key={comic.id}
                  to={`/article/${comic.id}`}
                  className="group flex gap-4 bg-background-50 rounded-xl p-4 border border-background-200/70 hover:border-primary-200 transition-all cursor-pointer"
                >
                  <div className="w-16 h-20 md:w-20 md:h-28 flex-shrink-0 rounded-lg overflow-hidden bg-background-100">
                    <img
                      src={comic.coverImage}
                      alt={`${comic.title} 韩国漫画`}
                      title={`${comic.title} - 韩国漫画最新章节`}
                      className="w-full h-full object-cover object-top"
                    />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="text-xs font-bold text-primary-600 bg-primary-100/70 px-2 py-0.5 rounded">
                        #{index + 1}
                      </span>
                      <span className="text-xs text-foreground-400">{comic.updateDate}</span>
                    </div>
                    <h3 className="text-sm font-bold font-heading text-foreground-900 group-hover:text-primary-600 transition-colors mb-1">
                      {comic.title}
                    </h3>
                    <p className="text-xs text-foreground-500 line-clamp-2 mb-2">{comic.description}</p>
                    <div className="flex items-center gap-3 text-xs text-foreground-400">
                      <span>
                        <i className="ri-user-line mr-1"></i>
                        {comic.author}
                      </span>
                      <span>
                        <i className="ri-book-open-line mr-1"></i>
                        {comic.chapters}话
                      </span>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="relative py-16 md:py-24 overflow-hidden">
          <img
            src="https://readdy.ai/api/search-image?query=A%20warm%20artistic%20abstract%20background%20with%20soft%20gradient%20shapes%20in%20terracotta%20and%20cream%20tones%20flowing%20organic%20forms%20minimalist%20elegant%20composition%20with%20gentle%20warm%20light%20dreamy%20atmosphere%20digital%20art%20style&width=1600&height=600&seq=cta-bg-home-02&orientation=landscape"
            alt=""
            className="absolute inset-0 w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-black/60 to-black/40"></div>
          <div className="relative z-10 text-center px-4 max-w-2xl mx-auto">
            <h2 className="text-2xl md:text-3xl font-bold font-heading text-white mb-4">
              随时随地，畅享韩漫阅读
            </h2>
            <p className="text-sm md:text-base text-white/70 mb-8">
              下载<strong>好看韩国漫画APP</strong>，在手机上也能流畅阅读无删减韩国漫画，支持离线缓存、夜间模式等实用功能
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
              <a
                href="#"
                className="px-8 py-3 bg-primary-500 text-background-50 dark:text-foreground-950 text-sm font-semibold rounded-lg hover:bg-primary-600 transition-all whitespace-nowrap cursor-pointer"
              >
                <i className="ri-android-line mr-2"></i>
                Android下载
              </a>
              <a
                href="#"
                className="px-8 py-3 bg-white/15 backdrop-blur-sm text-white text-sm font-semibold rounded-lg hover:bg-white/25 transition-all whitespace-nowrap cursor-pointer border border-white/20"
              >
                <i className="ri-apple-line mr-2"></i>
                iOS下载
              </a>
            </div>
          </div>
        </section>

        {/* FAQ Section */}
        <section className="py-14 md:py-20 bg-background-50">
          <div className="w-full px-4 md:px-6 lg:px-10 max-w-3xl mx-auto">
            <div className="text-center mb-10">
              <h2 className="text-2xl md:text-3xl font-bold font-heading text-foreground-900 mb-3">
                常见问题
              </h2>
              <p className="text-sm text-foreground-500">
                关于韩漫大全的常见疑问解答
              </p>
            </div>
            <div className="space-y-3">
              {faqs.map((faq, index) => (
                <details
                  key={index}
                  className="group bg-background-100 rounded-xl border border-background-200/70 overflow-hidden"
                >
                  <summary className="flex items-center justify-between px-5 py-4 cursor-pointer list-none">
                    <h3 className="text-sm font-bold font-heading text-foreground-800 pr-4">
                      {faq.question}
                    </h3>
                    <span className="w-6 h-6 flex items-center justify-center flex-shrink-0 text-foreground-400 group-open:rotate-45 transition-transform">
                      <i className="ri-add-line"></i>
                    </span>
                  </summary>
                  <div className="px-5 pb-4">
                    <p className="text-sm text-foreground-600 leading-relaxed">{faq.answer}</p>
                  </div>
                </details>
              ))}
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </>
  );
}